import { useEffect, useRef } from 'react';
import { getCookie } from 'cookies-next';

import { FEATURE_FLAG_NAMES } from '@/entities/feature-flags/constants';
import { useFeatureFlags } from '@/entities/feature-flags/hooks/useFeatureFlags';
import {
    CONSENT_SIGNAL_UNSUPPORTED,
    TrackingCategory,
    TrackingType,
} from '@/entities/tracking/const';
import { BootstrapSignalKeys, BootstrapSignals } from '@/entities/tracking/lib/bootstrap-signals';
import useTrackEvent from '@/hooks/tracking/useTrackEvent';
import { appLogger } from '@/utils/app-logger/appLogger';
import { CONSENT_COOKIE_KEY } from '@/utils/constants/common';
import { getClientLOB, isKetchCmpActive } from '@/utils/server/lob-utils';
import { getConsentStateFromCookie } from '@/utils/tracking/getConsentStateFromCookie';
import { isCmpSdkLoaded } from '@/utils/tracking/isCmpSdkLoaded';
import {
    initCmpConsentBridge,
    logCmpConsentConsumer,
    subscribeCmpConsent,
} from '@/utils/vendors/cmpConsentBridge';

const REGISTRATION_TIMEOUT_MS = 15_000;

/**
 * Consent state assumed when the CMP fails and no consent cookie exists —
 * [targeting, performance, functional, social].
 * A Ketch/Optanon failure (script blocked, CDN outage, `onScriptsReady` never firing) must not
 * silently suppress downstream tracking, so every category is reported as opted in and
 * CONSENT_UPDATED is still emitted on page load. When the user has previously saved consent
 * (consent cookie present), those saved values are respected instead — see
 * getConsentStateFromCookie.
 */
const CMP_FAILURE_CONSENT_STATE = [true, true, true, true];

type DoNotTrackValue = boolean | 'null' | 'unsupported';

type NavigatorWithGpc = Navigator & { globalPrivacyControl?: boolean };

const resolveDoNotTrack = (): DoNotTrackValue => {
    if (typeof navigator === 'undefined') return 'unsupported';

    if ('globalPrivacyControl' in navigator) {
        const gpc = (navigator as NavigatorWithGpc).globalPrivacyControl;
        return gpc ?? 'null';
    }

    if ('doNotTrack' in navigator) {
        if (navigator.doNotTrack === '1') return true;
        if (navigator.doNotTrack === '0') return false;
        return 'null';
    }

    return 'unsupported';
};

/**
 * Maps CMP consent state array [targeting, performance, functional, social] to tracking payload.
 */
const consentStateToPayload = (consentState: boolean[]) => ({
    consent: {
        marketingOptedIn: consentState[0],
        performanceOptedIn: consentState[1],
        functionalOptedIn: consentState[2],
        socialOptedIn: consentState[3],
        doNotSell: CONSENT_SIGNAL_UNSUPPORTED,
        doNotTrack: resolveDoNotTrack(),
        limitedAdTracking: CONSENT_SIGNAL_UNSUPPORTED,
    },
});

/**
 * Registers Ketch consent listeners and tracks consent state on initial load and
 * when the user changes preferences.
 * Gated by tn_ketch_enabled feature flag.
 * Must be used within TrackingProvider (e.g. inside InitTracking).
 * Subscribes via the central CMP consent bridge (with replay for late mounts).
 * If the CMP fails to resolve within REGISTRATION_TIMEOUT_MS, CONSENT_UPDATED is still emitted
 * with the values saved in the consent cookie when present, or with every consent category
 * opted in otherwise (see CMP_FAILURE_CONSENT_STATE).
 */
const useConsentTracking = () => {
    const { getFeatureFlag, isLoading: isFlagsLoading } = useFeatureFlags();
    const trackEvent = useTrackEvent();
    const trackEventRef = useRef(trackEvent);
    const hasMarkedRef = useRef(BootstrapSignals.has(BootstrapSignalKeys.CONSENT_UPDATED));
    const hasSentConsentEventRef = useRef(false);
    const latestSendIdRef = useRef(0);
    const isOptanonReadyRef = useRef(false);

    const ketchEnabled = getFeatureFlag(FEATURE_FLAG_NAMES.KETCH_ENABLED);
    // PTV loads the Ketch + Optanon CMP unconditionally (initCbsCmpScriptsServer only applies
    // the tn_ketch_enabled fallback to pplus), so PTV consent tracking must not depend on the
    // flag — otherwise regions where the flag is unset (e.g. BR) never emit CONSENT_UPDATED.
    // pplus still gates on the flag (flag-off = legacy OneTrust CMP path).
    const consentTrackingEnabled = isKetchCmpActive(getClientLOB(), Boolean(ketchEnabled));
    trackEventRef.current = trackEvent;

    const markConsentGate = () => {
        if (!hasMarkedRef.current) {
            hasMarkedRef.current = true;
            BootstrapSignals.mark(BootstrapSignalKeys.CONSENT_UPDATED);
        }
    };

    const sendConsentEvent = (privacyValues: Record<string, unknown>) => {
        hasSentConsentEventRef.current = true;
        const sendId = ++latestSendIdRef.current;

        const emit = () => {
            // A queued emission is stale once fresher consent state has been sent — without
            // this, a CMP that recovers after the failure fallback would have its real state
            // overwritten by the deferred all-opted-in payload.
            if (sendId !== latestSendIdRef.current) return;

            trackEventRef.current({
                type: TrackingType.CONSENT_UPDATED,
                detail: privacyValues,
                category: TrackingCategory.App.OPERATION,
            });
            markConsentGate();
        };

        if (hasMarkedRef.current) {
            emit();
        } else {
            void BootstrapSignals.whenAll([BootstrapSignalKeys.APP_INIT]).then(emit);
        }
    };

    // Bridge subscriptions — deps exclude isFlagsLoading so re-subscribing (and bridge replay)
    // does not duplicate CONSENT_UPDATED when flags settle after PTV initial consent.
    useEffect(() => {
        if (typeof window === 'undefined' || hasMarkedRef.current || !consentTrackingEnabled) {
            return;
        }

        initCmpConsentBridge();

        let readGeneration = 0;
        let lastSentChoiceCookie: string | undefined;

        const resolveConsentState = async (
            cmp: CbsOptanonCmp,
        ): Promise<{ consentState: boolean[]; savedChoiceCookie?: string }> => {
            // Ketch persists consent to `_ketch_consent_v1_` before firing bridge events.
            // `cmp.ot.getState()` can lag on slow networks (especially after consentChanged),
            // so prefer the persisted snapshot when available.
            const persisted = getConsentStateFromCookie();
            if (persisted !== null) {
                return {
                    consentState: persisted,
                    savedChoiceCookie: getCookie(CONSENT_COOKIE_KEY),
                };
            }
            return { consentState: await cmp.ot.getState() };
        };

        const emitConsentFromCmp = async (
            cmp: CbsOptanonCmp,
            source: 'initialConsent' | 'consentChanged',
        ) => {
            const generation = ++readGeneration;
            try {
                const { consentState, savedChoiceCookie } = await resolveConsentState(cmp);
                if (generation !== readGeneration) {
                    return;
                }
                // An early choice arrives as both initialConsent and consentChanged. Compare cookies,
                // not values, so a Reject after the all-false pre-choice event is still reported.
                if (savedChoiceCookie !== undefined && savedChoiceCookie === lastSentChoiceCookie) {
                    return;
                }
                lastSentChoiceCookie = savedChoiceCookie;
                const payload = consentStateToPayload(consentState);
                logCmpConsentConsumer('tracking', source, payload);
                sendConsentEvent(payload);
            } catch (error) {
                if (generation !== readGeneration) {
                    return;
                }
                appLogger.error(
                    'CMP getState failed',
                    { component: 'useConsentTracking', source },
                    error,
                );
            }
        };

        const unsubscribeScriptsReady = subscribeCmpConsent('scriptsReady', () => {
            isOptanonReadyRef.current = true;
        });
        const unsubscribeInitialConsent = subscribeCmpConsent('initialConsent', (cmp) => {
            void emitConsentFromCmp(cmp, 'initialConsent');
        });
        const unsubscribeConsentChanged = subscribeCmpConsent('consentChanged', (cmp) => {
            void emitConsentFromCmp(cmp, 'consentChanged');
        });

        return () => {
            unsubscribeScriptsReady();
            unsubscribeInitialConsent();
            unsubscribeConsentChanged();
        };
    }, [consentTrackingEnabled]);

    // CMP-failure fallback and flag-off gate — re-runs when isFlagsLoading settles to re-arm the
    // deadline without touching bridge subscriptions.
    useEffect(() => {
        if (typeof window === 'undefined' || hasMarkedRef.current) {
            return;
        }

        // pplus with tn_ketch_enabled off: release the pageview gate without CMP listeners.
        // PTV always has consentTrackingEnabled true (isKetchCmpActive ignores the flag).
        if (!isFlagsLoading && !consentTrackingEnabled) {
            markConsentGate();
            return;
        }

        if (!consentTrackingEnabled) {
            return;
        }

        // The CMP failed if nothing has been emitted by the deadline — either it never became
        // available, or it registered but `awaitInitialConsent` never resolved. Emit the
        // fully-opted-in fallback so consumers get a CONSENT_UPDATED on page load and the
        // pageview gate isn't held until the whenAll safety timeout.
        const failureTimeoutId = window.setTimeout(() => {
            if (hasSentConsentEventRef.current) return;
            // A loaded CMP that has not resolved is not a failure — an explicit-consent banner
            // is simply awaiting interaction. Fabricating consent here would claim opt-in while
            // the user is still looking at the banner, and would contradict loadTealiumClient,
            // which holds utag back in exactly this state. Release the gate so bootstrap is not
            // blocked; the real values emit via awaitInitialConsent whenever the user decides.
            if (isOptanonReadyRef.current && isCmpSdkLoaded()) {
                markConsentGate();
                return;
            }
            // Previously saved consent must survive a CMP failure: prefer the values from
            // the consent cookie and only fall back to all-opted-in when no cookie exists.
            const cookieConsentState = getConsentStateFromCookie();
            const fallbackPayload = consentStateToPayload(
                cookieConsentState ?? CMP_FAILURE_CONSENT_STATE,
            );
            appLogger.warn('CMP consent timeout fallback', {
                component: 'useConsentTracking',
                optanonReady: isOptanonReadyRef.current,
                cmpSdkLoaded: isCmpSdkLoaded(),
            });
            logCmpConsentConsumer('tracking', 'timeout-fallback', fallbackPayload);
            sendConsentEvent(fallbackPayload);
            // Release the gate at the deadline even though the event itself still queues behind
            // APP_INIT: a dead CMP must never hold bootstrap dependents open for a second
            // whenAll timeout. Order matters — this must run AFTER sendConsentEvent, which only
            // takes the APP_INIT-ordered path while the gate is still unmarked.
            markConsentGate();
        }, REGISTRATION_TIMEOUT_MS);

        return () => {
            window.clearTimeout(failureTimeoutId);
        };
    }, [consentTrackingEnabled, isFlagsLoading]);
};

export default useConsentTracking;
